import type { InfiniteData } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import type {
  AuditRecommendationLocation,
  AuditRecommendationStatus,
  QueryAuditRecommendationsResponseBodyType,
} from "@intelligems/ig-types/functions/opportunity-audit/records/recommendations";
import {
  favoriteRecommendationsKey,
  queryAuditRecommendations,
  saveRecommendationsFromConversation,
  setRecommendationState,
} from "@src/api/opportunity-audit/audit-recommendations";
import { isUserAlertPending, setUserAlerts } from "@src/api/users/user-alerts";
import { queryClient } from "@src/queryClient";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import {
  getAtlasConnection,
  getRecommendationStatus,
  getRecommendationStatuses,
  recommendationStatusesSet,
  savedNoticeOpened,
} from "@src/store/slices/opportunityAudit";
import type { AuditRecommendationStatusEntry } from "@src/store/slices/opportunityAudit/types";

/** The query takes at most this many keys, and answers this many rows when asked. */
const BATCH = 200;

export interface RecommendationStatusesApi {
  query: typeof queryAuditRecommendations;
  setState: typeof setRecommendationState;
  saveFromConversation: typeof saveRecommendationsFromConversation;
  setUserAlerts: typeof setUserAlerts;
  isUserAlertPending: typeof isUserAlertPending;
}

const httpStatusOf = (error: unknown) =>
  isAxiosError(error) ? error.response?.status : undefined;

const favoritesChanged = () =>
  void queryClient.invalidateQueries({
    queryKey: favoriteRecommendationsKey,
  });

/**
 * Takes a recommendation out of the saved ideas list at once. A refetch
 * already on its way would put it back, so it is cancelled first.
 */
const favoriteRemoved = async (key: string) => {
  await queryClient.cancelQueries({ queryKey: favoriteRecommendationsKey });
  queryClient.setQueryData<
    InfiniteData<QueryAuditRecommendationsResponseBodyType>
  >(
    favoriteRecommendationsKey,
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          recommendations: page.recommendations.filter(
            (recommendation) => recommendation.id !== key
          ),
        })),
      }
  );
};

const loaded = (
  status: AuditRecommendationStatus | null
): AuditRecommendationStatusEntry => ({
  type: "loaded",
  status: status && statusOf(status),
});

/**
 * The one place the panel's recommendation statuses are written, from every
 * source: a load, a recommendation's live save, a favorite, and a websocket
 * message.
 */
export const createRecommendationStatuses = (
  api: RecommendationStatusesApi
) => {
  /**
   * Reads the statuses of these keys. A key with no row reads as loaded with
   * no status only once every batch has answered; a batch that fails leaves
   * its keys unloaded, to be read again the next time the panel opens.
   */
  const loadRecommendationStatuses = createAsyncThunk<void, string[]>(
    "opportunityAudit/loadRecommendationStatuses",
    async (keys, { dispatch }) => {
      const unique = [...new Set(keys)];
      if (unique.length === 0) {
        return;
      }
      const found: Record<string, AuditRecommendationStatusEntry> = {};
      let complete = true;
      for (let at = 0; at < unique.length; at += BATCH) {
        const batch = unique.slice(at, at + BATCH);
        try {
          let cursor: string | undefined;
          do {
            const page = await api.query({
              ids: batch,
              limit: BATCH,
              ...(cursor ? { cursor } : {}),
            });
            for (const status of page.recommendations) {
              found[status.id] = loaded(status);
            }
            cursor = page.next ?? undefined;
          } while (cursor);
        } catch {
          complete = false;
        }
      }
      if (complete) {
        for (const key of unique) {
          found[key] ??= loaded(null);
        }
      }
      dispatch(recommendationStatusesSet(found));
    }
  );

  /**
   * Saves recommendations the open conversation showed that node-backend has
   * no row for, through web/ai, which reads them from the conversation. True
   * when every one of them is now saved.
   */
  const saveFromConversation = createAsyncThunk<boolean, string[]>(
    "opportunityAudit/saveFromConversation",
    async (keys, { dispatch, getState }) => {
      const { sessionId } = getAtlasConnection(getState());
      if (!sessionId) {
        return false;
      }
      try {
        const { recommendations } = await api.saveFromConversation({
          sessionId,
          recommendationIds: keys,
        });
        dispatch(
          recommendationStatusesSet(
            Object.fromEntries(
              recommendations.map((status) => [status.id, loaded(status)])
            )
          )
        );
        const saved = new Set(recommendations.map((status) => status.id));
        return keys.every((key) => saved.has(key));
      } catch {
        return false;
      }
    }
  );

  /**
   * Marks a recommendation as a favorite, or not. One with no row is saved
   * from the conversation first. An unsaved one leaves the saved ideas list at
   * once. A failure leaves it `updateFailed`, so it can be tried again, and
   * reloads the list.
   */
  const setFavorite = createAsyncThunk<
    void,
    { key: string; favorite: boolean; location: AuditRecommendationLocation }
  >(
    "opportunityAudit/setFavorite",
    async ({ key, favorite, location }, { dispatch, getState }) => {
      const status = getRecommendationStatus(getState(), key)?.status ?? null;
      dispatch(
        recommendationStatusesSet({ [key]: { type: "updating", status } })
      );
      if (!favorite) {
        await favoriteRemoved(key);
      }
      const setState = async () => {
        const changed = await api.setState({
          recommendationId: key,
          state: favorite ? "favorite" : "presented",
          location,
        });
        dispatch(recommendationStatusesSet({ [key]: loaded(changed.status) }));
        favoritesChanged();
        // A save on the Dashboard is already in view there.
        if (favorite && location !== "saved_ideas") {
          const notice = api.isUserAlertPending(
            "showSavedRecommendationNotice"
          );
          api.setUserAlerts({
            unseenOnDashboard: true,
            ...(notice && { showSavedRecommendationNotice: false }),
          });
          if (notice) {
            dispatch(savedNoticeOpened());
          }
        }
      };
      try {
        try {
          await setState();
        } catch (error) {
          if (
            httpStatusOf(error) !== 404 ||
            !(await dispatch(saveFromConversation([key])).unwrap())
          ) {
            throw error;
          }
          await setState();
        }
      } catch {
        dispatch(
          recommendationStatusesSet({ [key]: { type: "updateFailed", status } })
        );
        favoritesChanged();
      }
    }
  );

  /** A recommendation's status as its live save carried it. */
  const statusFromProgress = createAsyncThunk<void, AuditRecommendationStatus>(
    "opportunityAudit/statusFromProgress",
    (status, { dispatch }) => {
      dispatch(recommendationStatusesSet({ [status.id]: loaded(status) }));
    }
  );

  /**
   * The statuses the saved ideas list carried. One with a change on its way
   * keeps that change.
   */
  const savedIdeasLoaded = createAsyncThunk<void, AuditRecommendationStatus[]>(
    "opportunityAudit/savedIdeasLoaded",
    (statuses, { dispatch, getState }) => {
      dispatch(
        recommendationStatusesSet(
          Object.fromEntries(
            statuses
              .filter(
                (status) =>
                  getRecommendationStatus(getState(), status.id)?.type !==
                  "updating"
              )
              .map((status) => [status.id, loaded(status)])
          )
        )
      );
    }
  );

  /** A status changed elsewhere, such as the link a test's save wrote. */
  const recommendationUpdated = createAsyncThunk<
    void,
    AuditRecommendationStatus
  >(
    "opportunityAudit/recommendationUpdated",
    (status, { dispatch, getState }) => {
      if (status.id in getRecommendationStatuses(getState())) {
        dispatch(recommendationStatusesSet({ [status.id]: loaded(status) }));
      }
      favoritesChanged();
    }
  );

  return {
    loadRecommendationStatuses,
    saveFromConversation,
    setFavorite,
    statusFromProgress,
    savedIdeasLoaded,
    recommendationUpdated,
  };
};

/** Only the status fields, whatever else the answer carried. */
const statusOf = (
  status: AuditRecommendationStatus
): AuditRecommendationStatus => ({
  id: status.id,
  state: status.state,
  stateAtTs: status.stateAtTs,
  stateByUserId: status.stateByUserId,
  experience: status.experience,
  createdAtTs: status.createdAtTs,
});

export const {
  loadRecommendationStatuses,
  saveFromConversation,
  setFavorite,
  statusFromProgress,
  savedIdeasLoaded,
  recommendationUpdated,
} = createRecommendationStatuses({
  query: queryAuditRecommendations,
  setState: setRecommendationState,
  saveFromConversation: saveRecommendationsFromConversation,
  setUserAlerts,
  isUserAlertPending,
});
